// src/i18n/translations/en.js

export default {
  // General elements and actions
  common: {
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    loading: 'Loading',
    success: 'Success',
    error: 'Error',
    confirm: 'Confirm',
    warning: 'Warning',
    info: 'Info',
    close: 'Close',
    cancel: 'Cancel',
    changeLanguage: 'Change language',
    changingLanguage: 'Changing language...',
    languageInfoDescription: 'Language changes are applied instantly and reflect on all content. Your preference is automatically saved.',
    quickAccess: 'Quick Access',
    closeMenu: 'Close menu',
    tools: 'Tools',
    me: 'Me',
    add: 'Add',
    copy: 'Copy',
    copyLink: 'Copy Link',
    share: 'Share',
    copyFailed: 'Copy failed:',
    name: 'Name',
    user: 'User',
    continue: 'Continue',
    processing: 'Processing...',
    change: 'Change',
    copied: 'Copied',
    understand: 'I understand',
    ok: 'OK',
    back: 'Back',
    logoAlt: 'IDapps Logo', // Added
    version: 'IDapps v2.0.31', // Added
    digitalDiscipline: 'Digital Discipline' // Added Digital Discipline translation
  },

  // Digital Discipline section translations
  digitalDiscipline: {
    panelTitle: 'Digital Discipline Panel',
    panelDescription: 'Control your digital habits, achieve your goals',
    newGoal: 'Create New Goal',
    noGoals: 'You have no goals yet',
    noGoalsInCategory: 'You have no goals in this category',
    createGoal: 'Create Goal',
    startGoal: 'Start',
    cancel: 'Cancel',
    save: 'Save',
    title: 'Title',
    titlePlaceholder: 'E.g.: Read for 1 hour',
    category: 'Category',
    duration: 'Duration (minutes)',
    earnedXP: 'XP to earn',
    automaticCalc: 'Automatically calculated based on duration and category',
    totalXP: 'Total XP',
    activeTimers: 'Active Timers',
    show: 'Show',
    hide: 'Hide',
    // Categories
    all: 'All',
    productivity: 'Productivity',
    health: 'Health',
    digital_detox: 'Digital Detox',
    learning: 'Learning',
    other: 'Other',

    // Tabs
    activeTab: "Active",
    completedTab: "Completed",
    statisticsTab: "Statistics",
    blockedAppsTab: "Blocked Apps",

    // Goals
    goals: "Goals",
    addGoal: "Add Goal",
    activateGoal: "Activate Goal",
    goalActive: "Goal Active",
    completeGoal: "Complete Goal",
    cancelGoal: "Cancel Goal",
    daysRemaining: "{days} days left",
    lastDay: "Last day",
    expired: "Expired",

    // Categories
    productivity: "Productivity",
    health: "Health",
    learning: "Learning",
    digitalDetox: "Digital Detox",
    mindfulness: "Mindfulness",
    habit: "Habit",
    other: "Other",

    // Focus session
    focusSession: "Focus Session",
    startFocus: "Start Focus",
    pauseFocus: "Pause",
    resumeFocus: "Resume",
    endFocus: "End Session",
    focusComplete: "Focus Complete!",
    earnedXP: "You earned {xp} XP!",
    cancelPenalty: "Early cancellation will result in a {penalty}% XP penalty",

    // Time format
    minutesFormat: "{minutes} minutes",

    // Blocked apps
    blockedApps: "Blocked Apps",
    addBlockedApp: "Add Blocked App",
    dailyLimit: "Daily Limit",
    blockUntil: "Block Until",
    category: "Category",
    socialMedia: "Social Media",
    video: "Video",
    games: "Games",

    // Statistics
    screenTime: "Screen Time",
    completedGoals: "Completed Goals",
    focusTime: "Focus Time",
    earnedPoints: "Earned XP",
    level: "Level",
    streak: "Streak",

    // AI suggestions
    aiSuggestions: "AI Suggestions",
    tryNow: "Try Now",
    dismiss: "Dismiss",

    // Error messages
    titleError: 'Title cannot be empty',
    durationError: 'Duration must be at least 1 minute',
    titleHint: 'Enter your goal in a clear and concise way',
  },

  // Slogans - Added for dynamic slogan support
  slogan: {
    decentralized: "World's first decentralized identity solution",
    evolution: "The next step in identity evolution",
    control: "Take back control of your data",
    secure: "The secure fortress of your digital identity",
    realIdentity: "Bring your real identity to the digital world",
    assets: "All your digital assets in one place",
    dataOwnership: "Your personal data belongs to you",
    future: "Welcome to the decentralized future",
    youBelong: "What makes you you belongs to you",
    welcome: "Welcome to IDapps"
  },

  // Navigation and Menu items
  nav: {
    dashboard: 'Home',
    profile: 'Profile',
    digitalVault: 'Digital Vault',
    settings: 'Settings',
    twoFA: 'Two-Factor Authentication',
    qrCode: 'QR Code',
    securitySettings: 'Security Settings',
    notifications: 'Notifications',
    vault: 'Digital Vault',
    qr: 'QR',
    messages: 'Messages',
    gameHub: 'Games',
    statistics: 'Statistics' // Added: Statistics navigation translation
  },

  // Welcome screen
  welcome: {
    title: 'World\'s first decentralized identity',
    subtitle: 'Join IDapps and enter the new world',
    loginButton: 'Login',
    signupButton: 'Create account',
    footer: {
      description: 'Decentralized blockchain-powered identity platform',
      version: 'v1.0.1 © 2023-2025 IDapps'
    },
    welcomeTitle: 'Welcome',
    futureFeature: 'Coming soon: secure biometric identity verification',
    headline: 'World\'s first decentralized identity',
    subtitle: 'Take control of your digital identity',
    heroTitle: 'Your Identity,\nReimagined.',
    getStarted: 'Get Started',
    haveAccount: 'Already have an account?',
    login: 'Log In',
    signInWithApple: 'Sign in with Apple',
  },

  // Login screen and authentication
  login: {
    title: 'World\'s first decentralized identity',
    subtitle: 'Join IDapps and enter the new world',
    usernameInput: 'Username or email',
    formError: {
      emptyFields: 'Please enter username and password.',
      default: 'Login failed. Please check your username and password.',
      demoFailed: 'Demo login failed.'
    },
    loggingIn: 'Logging in...',
    loginButton: 'Login',
    demoLoginDone: 'Demo Login Completed',
    demoLogin: 'Demo Login (Developer)',
    forgotPassword: 'Forgot password',
    noAccount: 'Don\'t have an IDapps account yet?',
    createAccount: 'Create account',
    loginTitle: 'Login',
    welcomeBack: 'Welcome Back',
    subtitle: 'Sign in to access your digital vault',
    username: 'Username',
    email: 'Email',
    password: 'Password',
    passwordPlaceholder: 'Enter your password',
    forgot: 'Forgot Password?',
    submit: 'Sign In',
    processing: 'Processing...',
    create: 'Create Identity',
    error: {
      empty: 'Please fill in all fields',
      failed: 'Login failed. Check your credentials.'
    }
  },

  // Registration screen
  signup: {
    title: 'World\'s first decentralized identity',
    subtitle: 'Join IDapps and enter the new world',
    createIdappsAccount: 'Create IDapps account',
    createAccountWithApple: 'CREATE ACCOUNT WITH APPLE',
    createAccountWithFacebook: 'CREATE ACCOUNT WITH FACEBOOK',
    alreadyHaveAccount: 'Already have an IDapps account?',
    signupTitle: 'Sign Up',
    joinFuture: 'Join the Future',
    subtitle: 'Join the decentralized identity network',
    name: 'Full Name',
    email: 'Email Address',
    password: 'Password',
    submit: 'Create Account',
    processing: 'Creating Account...',
    or: 'Or continue with',
    hasAccount: 'Already have an account?',
    login: 'Sign In',
    confirmPassword: 'Confirm Password',
    phoneNumber: 'Phone Number',
    verificationCode: 'Verification Code',
    continueButton: 'Continue',
    createAccount: 'Create Account',
    enterPhone: 'Enter your phone number',
    phonePlaceholder: 'Phone number',
    invalidPhone: 'Invalid phone number',
    createAccountWithVoice: 'Create Account with Voice Registration',

    // New Signup Flow Keys
    nameTitle: "What's your name?",
    nameSubtitle: "This will be displayed on your profile",
    namePlaceholder: "Your full name",

    emailTitle: "What's your email?",
    emailSubtitle: "We'll use this for account verification",
    emailPlaceholder: "name@example.com",

    passwordTitle: "Create a password",
    passwordSubtitle: "Make it strong and unique",
    passwordPlaceholder: "Minimum 8 characters",

    confirmTitle: "Confirm details",
    confirmSubtitle: "Make sure everything is correct",

    complete: "Create Account",

    haveAccount: "Already have an account?",

    error: {
      nameRequired: "Name is required",
      emailRequired: "Email is required",
      emailInvalid: "Invalid email address",
      passwordLength: "Password must be at least 8 characters",
      generic: "Registration failed. Please try again.",
      empty: 'Please fill in all fields',
      failed: 'Registration failed. Please try again.'
    }
  },

  // Onboarding Flow
  onboarding: {
    step1: {
      title: "Biggest need?",
      title_short: "main focus?",
      subtitle: "What do you need help with?"
    },
    step2: {
      title: "Morning habits",
      subtitle: "Routines that energize you."
    },
    step3: {
      title: "Daily flow",
      subtitle: "Balance work and health."
    },
    step4: {
      title: "Weekly plans",
      subtitle: "What fits your week?"
    },
    step5: {
      title: "Updates?",
      subtitle: "Stay in the loop."
    },
    needs: {
      organize: "Organize Day & Time",
      remember: "Remember Tasks",
      prioritize: "Prioritize To-Dos",
      routines: "Build Routines",
      focus: "Deep Focus",
      other: "Something else"
    },
    habits: {
      wakeup: "Wake up",
      prayer: "Prayer",
      bed: "Make bed",
      water: "Water",
      shower: "Shower",
      coffee: "Coffee",
      breakfast: "Breakfast",
      plan: "Plan",
      commute: "Commute"
    },
    flow: {
      plan: "Plan",
      study: "Study",
      work: "Work",
      emails: "Emails",
      deepwork: "Deep Work",
      lunch: "Lunch",
      coffee: "Coffee",
      water: "Water",
      workout: "Sport",
      grocery: "Shop",
      socialize: "Social",
      commute: "Commute"
    },
    plans: {
      work: "Work/School",
      todos: "To-dos",
      social: "Social",
      home: "Home",
      placeholder: "Or write your own..."
    },
    updates: {
      no_short: "No updates",
      no_desc: "I'll check manually",
      yes_short: "Notify me",
      yes_desc: "News & Offers"
    },
    suggest: "Suggest for me",
    privacy_badge: "Encrypted on Device"
  },

  // Translations for VoiceSignupScreen
  voiceSignup: {
    title: 'IDapps Digital Identity Assistant',
    welcome: 'Hello, I\'m your IDapps digital identity assistant. We\'ll create your decentralized identity account together. When you\'re ready, you can say "let\'s begin".',
    askName: 'Great! First, can I know your name?',
    enterName: 'Please enter your name',
    askEmail: 'Thank you {name}. Now, can you tell me your email address?',
    enterEmail: 'Thank you {name}. Please enter your email address',
    askPhone: 'Can I also have your phone number?',
    enterPhone: 'Please enter your phone number',
    askPassword: 'Now it\'s time to set a secure password. For your security, you must enter your password using the keyboard. It should contain at least 8 characters, uppercase and lowercase letters, and numbers.',
    enterPassword: 'Please set a secure password (at least 8 characters, including uppercase, lowercase, and numbers)',
    passwordTooShort: 'Password must be at least 8 characters. Please try again.',
    confirmVoice: 'I have all your information {name}. I\'m ready to create your IDapps account. Do you confirm?',
    confirmText: 'I have all your information {name}. I\'m ready to create your IDapps account.',
    reviewInfo: 'I understand, let\'s review your information again.',
    successVoice: 'Great! I\'ve successfully created your IDapps account. Welcome to the decentralized identity world!',
    successText: 'Your IDapps account has been successfully created. Welcome to the decentralized identity world!',
    errorVoice: 'An error occurred while creating the account. Please try again.',
    errorText: 'An error occurred while creating the account. Please try again.',
    listening: 'I\'m listening...',
    textModeAuto: 'Your browser doesn\'t provide voice support. Text form will be used.',
    textModeEnabled: 'Switched to text form mode. Please enter your name.',
    textModeFallback: 'There was a problem with voice recognition. Text form will be used.',
    noSpeechSupport: 'Your browser doesn\'t support speech recognition. You need to use the text form.',
    form: {
      name: 'Full Name',
      email: 'Email Address',
      phone: 'Phone Number',
      password: 'Password',
      namePlaceholder: 'Full Name',
      emailPlaceholder: 'example@mail.com',
      phonePlaceholder: '5XX XXX XX XX',
      continue: 'Continue',
      completeRegistration: 'Create Account',
      registering: 'Registering...'
    },
    accessibility: {
      hint: 'If you are hearing impaired or don\'t want to use voice registration',
      switchToText: 'Switch to text form',
      switchToVoice: 'Switch to voice mode'
    }
  },

  // Profile section
  profile: {
    personalInfo: 'Personal Information',
    bloodType: 'Blood Type',
    height: 'Height',
    weight: 'Weight',
    heightWeight: 'Height / Weight',
    languages: 'Languages',
    contacts: 'Contacts',
    bio: 'About Me',
    editProfile: 'Edit Profile',
    identity: 'Identity',
    profileTitle: 'Profile',
    qrId: 'My QR ID',
    tattoo: 'Tattoo',
    shareProfileQR: 'Show this QR code to share your profile',
    profileLinkCopied: 'Profile sharing link copied',
    shareProfileDescription: 'You can use this QR code to share {name}\'s profile'
  },

  // Security and verification
  security: {
    security: 'Security',
    securityStatus: 'Security Status',
    accountSecure: 'Your account is secure',
    securityMeasuresActive: 'All security measures are active.',
    twoFactorAuth: 'Two-Factor Authentication',
    active: 'Active',
    passwordStrength: 'Password Strength',
    strong: 'Strong',
    lastLogin: 'Last Login',
    today: 'Today',
    manageSecuritySettings: 'Manage Security Settings',
    blockchainSecurity: 'Blockchain Security',
    blockchainSecurityDesc: 'Your account is protected with extra security via blockchain wallet connection.',
    connectedWallet: 'Connected Wallet',
    securityVerification: 'Security Verification',
    biometricAuth: 'Biometric Authentication',
    setupTwoFA: '2FA Setup',
    scanQRCode: 'Scan QR Code',
    enterCode: 'Enter Code',
    recoveryKey: 'Recovery Key',
    backupCodes: 'Backup Codes',
    passwordAndSecurity: 'Password and Security',
    emailAndContact: 'Email and Contact',
    twoFactorAuthTitle: 'Two-Factor Authentication',
    securitySettings: 'Security Settings',
    endToEndEncryption: 'End-to-End Encryption',
    enhancedSecurityMode: 'Enhanced security mode active',
    maxSecurityMode: 'Maximum security mode active',
    endToEndEncryptionAccess: 'End-to-end encrypted secure access',
    secure: 'Secure',
    customQR: 'Custom QR',
    customQRCodes: 'Custom QR Codes',
    customQRDescription: 'Create custom QR codes for your frequently used links and share them quickly.',
    noCustomQR: 'You don\'t have any custom QR codes yet.',
    linkName: 'Link name',
    addNewQRCode: 'Add New QR Code',

    // Two-Factor Authentication section
    twoFA: {
      // Tabs
      tabs: {
        setup: 'Setup',
        codes: 'Codes',
        backup: 'Backup',
        devices: 'Devices'
      },

      // Status messages
      status: {
        enabled: '2FA Enabled',
        disabled: '2FA Not Enabled'
      },

      // Error messages
      errors: {
        walletRequired: 'You need to connect your blockchain wallet first to set up 2FA.',
        setupFailed: 'An error occurred during setup. Please try again.',
        invalidCode: 'Please enter the 6-digit verification code.',
        verificationFailed: 'Invalid verification code. Please try again.',
        generalError: 'An error occurred. Please try again.',
        codeRequired: 'Please enter the security verification code.',
        disableFailed: 'Could not disable 2FA. The code may be incorrect.',
        passwordTooShort: 'Please enter a password of at least 8 characters.',
        exportFailed: 'Could not export 2FA data.',
        noImportData: 'Please enter 2FA data to import.',
        passwordRequired: 'Please enter your password.',
        importFailed: 'Could not import 2FA data. The password may be incorrect.',
        deviceRemoveFailed: 'Could not remove device.'
      },

      // Success messages
      success: {
        verified: 'Verification successful! 2FA enabled.',
        disabled: '2FA successfully disabled.',
        exported: '2FA data successfully exported.',
        imported: '2FA data successfully imported.',
        deviceRemoved: 'Device successfully removed.',
        deviceVerified: 'Device successfully verified.',
        enabled: '2FA Enabled!',
        enabledDescription: 'Two-factor authentication successfully enabled. Your account is now more secure.'
      },

      // General labels and descriptions
      decentralized: 'Decentralized 2FA',
      description: 'Two-factor authentication (2FA) adds an extra layer of security to your account. This feature ensures your account is protected even if your password is compromised.',
      scanQRCode: 'Scan QR Code',
      scanQRDescription: 'Scan the QR code below using Google Authenticator, Authy, or a similar authentication app.',
      manualKeyInstructions: 'If you can\'t scan the QR code, you can enter this key manually:',
      verificationCode: 'Verification Code',
      enterSixDigitCode: 'Enter 6-digit code',
      disableInstructions: 'Two-factor authentication is currently enabled. To disable this feature, enter a verification code from your authentication app on your device.',
      currentCode: 'Current Code',
      codeRefreshTime: 'Refreshes in {seconds} seconds',
      codeRefreshing: 'Refreshing...',
      backupCodes: 'Backup Codes',
      backupCodesInstructions: 'You can use these backup codes when you don\'t have access to your 2FA device. Each code can only be used once.',
      backupCodesDescription: 'Keep these backup codes in a safe place. You can use them when you don\'t have access to your authentication app.',
      saveBackupCodes: 'Save Backup Codes',
      blockchainVerification: 'Blockchain Verification',
      blockchainVerificationDescription: 'Your 2FA verification proofs are securely stored and verified on the blockchain.',
      lastVerification: 'Last Verification',
      timeAgo: '{time} hours ago',
      verifiedOnPolygon: 'Verified on Polygon network',
      proofGenerated: 'Verification proof generated: {proof}...',
      enableFirstToViewCodes: 'You need to enable 2FA first to view verification codes.',
      backupAndRecovery: 'Backup and Recovery',
      backupDescription: 'You can export your 2FA data (encrypted) or import previously backed up data.',
      exportData: 'Export Data',
      exportDescription: 'Export your 2FA data encrypted with a strong password. Keep this file in a safe place.',
      password: 'Password',
      passwordPlaceholder: 'Enter your password (at least 8 characters)',
      encryptedData: 'Encrypted Data',
      storeDataSafely: 'Store this data safely. If you forget your password, you won\'t be able to recover the data.',
      importData: 'Import Data',
      importDescription: 'Import 2FA data that you previously exported. This will change your current 2FA settings.',
      pasteEncryptedData: 'Paste encrypted data that was exported',
      enterPassword: 'Enter your password',
      arweaveBackup: 'Arweave Backup',
      arweaveDescription: 'Your 2FA verification proofs are stored encrypted on Arweave. This provides a permanent and immutable backup.',
      arweaveBackupStarted: 'Arweave backup process started. This may take a few minutes.',
      decentralizedStorage: 'Decentralized Storage',
      arweaveTokenRequired: 'Arweave ensures your data is stored permanently and immutably. A small amount of AR token is required for the backup process.',
      verifiedDevices: 'Verified Devices',
      devicesDescription: 'You can manage your devices that have completed 2FA verification here.',
      noVerifiedDevices: 'No verified devices yet.',
      thisDevice: 'This Device',
      deviceVerificationDescription: 'The device you\'re using is automatically added to the list of verified devices. Regularly check your devices and remove any you don\'t recognize.',
      deviceConnectionHistory: 'Device Connection History',
      lastAccess: 'Last access: {time}',
      daysAgo: '{days} days ago',
      weeksAgo: '{weeks} weeks ago',

      // Device types
      deviceTypes: {
        mobile: 'Mobile Device',
        desktop: 'Desktop Device'
      },

      // Features section
      features: {
        highSecurity: {
          title: 'High Security',
          description: 'Authentication codes are generated with a secret key stored on your device.'
        },
        easyToUse: {
          title: 'Easy to Use',
          description: 'You can use any authentication app with QR code scanning.'
        },
        decentralized: {
          title: 'Decentralized',
          description: 'Security data is verified on the blockchain, not dependent on servers.'
        }
      },

      // Recovery info modal
      recoveryInfo: {
        title: 'Recovery Information',
        description: 'With 2FA recovery options, you can access your account even if you lose your device or delete your app:',
        backupCodes: {
          title: 'Backup Codes',
          description: 'Each backup code is valid for one-time use. Keep these codes in a safe place.'
        },
        encryptedBackup: {
          title: 'Encrypted Backup',
          description: 'You can export your 2FA data in encrypted form. Don\'t forget this password, otherwise the backup cannot be used.'
        },
        verifiedDevices: {
          title: 'Verified Devices',
          description: 'Your previously verified devices can help you in the authentication process.'
        }
      },

      // Remove device modal
      removeDevice: {
        title: 'Remove Device',
        confirmation: 'Are you sure you want to remove this device?'
      },

      // Buttons
      buttons: {
        startingSetup: 'Starting Setup',
        startSetup: 'Start 2FA Setup',
        verifying: 'Verifying...',
        verify: 'Verify',
        disable: 'Disable 2FA',
        copyCode: 'Copy Code',
        copyAll: 'Copy All',
        generateNewCodes: 'Generate New Codes',
        generateProof: 'Generate Verification Proof',
        goToSetup: 'Go to 2FA Setup',
        export: 'Export',
        import: 'Import',
        backupToArweave: 'Backup to Arweave',
        verifyThisDevice: 'Verify This Device'
      }
    }
  },

  // Translations for Auth Context
  auth: {
    login: {
      inProgress: 'Operation already in progress',
      credentialsRequired: 'Username and password are required.',
      error: 'An error occurred while logging in. Please try again.'
    },
    register: {
      inProgress: 'Operation already in progress',
      userDataRequired: 'Valid user information is required.',
      error: 'An error occurred during registration. Please try again.'
    },
    resetPassword: {
      inProgress: 'Operation already in progress',
      emailRequired: 'A valid email address is required.',
      success: 'Password reset link sent.',
      error: 'An error occurred during password reset. Please try again.'
    },
    profile: {
      dataRequired: 'Valid profile information is required.',
      updateError: 'An error occurred during profile update.'
    },
    navigation: {
      inProgress: 'Redirection is already in progress.',
      invalidScreen: 'Invalid screen name.',
      redirectError: 'Post-authentication redirection error:'
    },
    session: {
      checkError: 'Error checking session status:',
      loadError: 'An error occurred while loading session information.'
    },
    identity: {
      verificationFailed: 'Identity verification failed.',
      error: 'An error occurred during identity verification.'
    },
    security: {
      reAuthRequired: 'Re-authentication is required to access secure screens.',
      authRequired: 'Protected screens cannot be accessed without authentication.'
    },
    wallet: {
      connectError: 'An error occurred during wallet connection.',
      arweaveError: 'An error occurred during Arweave wallet connection.',
      walletRequired: 'Wallet connection is required for Arweave operations'
    },
    '2fa': {
      loadError: '2FA data could not be loaded automatically:',
      walletRequired: 'Wallet connection is required for 2FA setup',
      setupError: '2FA setup error:',
      notSetup: '2FA not yet set up',
      verifyError: '2FA verification error:',
      disableError: '2FA disable error:',
      getCodeError: '2FA code retrieval error:',
      exportError: '2FA data export error:',
      importError: '2FA data import error:',
      proofError: '2FA proof generation error:'
    },
    arweave: {
      uploadError: 'Arweave upload error:',
      downloadError: 'Arweave download error:',
      listError: 'Arweave transaction list error:',
      walletRequired: 'Wallet connection is required for Arweave operations'
    },
    notes: {
      saveError: 'Note saving error:',
      getError: 'Note retrieval error:'
    }
  },

  // Digital vault and notes
  vault: {
    vault: 'Vault',
    notes: 'Notes',
    backups: 'Backups',
    addNote: 'Add Note',
    searchNotes: 'Search Notes',
    noteTitle: 'Note Title',
    noteContent: 'Note Content',
    addTag: 'Add Tag',
    saveNote: 'Save Note',
    digitalVaultTitle: 'Digital Vault',
    endlessNotesTitle: 'Endless Notes',
    memoryPalace: 'Memory Palace'
  },

  // Application settings
  settings: {
    appSettings: 'App Settings',
    appSettingsDesc: 'Manage app behavior and preferences',
    accountSettings: 'Account Settings',
    accountSettingsDesc: 'Manage your profile information and preferences',
    languageSelection: 'Language Selection',
    selectLanguageDescription: 'Select the language you want to use the app in.',
    themeAndAppearance: 'Theme and Appearance',
    notificationSettings: 'Notification Settings',
    languageAndRegion: 'Language and Region',
    other: 'Other',
    helpAndSupport: 'Help and Support',
    aboutAndLegal: 'About and Legal',
    confirmLogout: 'Do you want to log out?',
    logoutDescription: 'You are about to log out of your account. You will need to log in again.',
    darkMode: 'Dark Mode', // Previously existed
    lightMode: 'Light Mode',  // Previously existed
    switchToLightMode: 'Switch to light mode', // Added
    switchToDarkMode: 'Switch to dark mode' // Added
  },

  // Account information
  account: {
    title: 'Enter account information',
    description: 'Enter account information to complete the account creation process.',
    gender: {
      male: 'Male',
      female: 'Female',
      other: 'Other'
    },
    error: {
      missingFields: 'Please fill in all required fields.',
      passwordMismatch: 'Passwords don\'t match.',
      passwordTooShort: 'Password must be at least 6 characters.'
    },
    termsAgreement: 'By creating an account, you accept the {terms} and {privacy}.',
    termsAndConditions: 'Terms and Conditions',
    privacyPolicy: 'User Privacy Agreement'
  },

  // Translations for Notifications section
  // dashboard section
  dashboard: {
    exhibitions: 'Exhibitions',
    identity: 'Identity',
    myFolders: 'My Folders',
    qrIdentity: 'QR Identity',
    badges: 'Badges',
    activity: 'Activity',
    active: 'Active',
    noItems: 'No items in this folder yet',
    addItem: 'Add Item',
    folders: {
      personal: 'Personal',
      health: 'Health',
      documents: 'Documents',
      favorite: 'Favorites'
    },
    emergencyInfo: 'Emergency Information',
    allergies: 'Allergies',
    healthConditions: 'Health Conditions',
    medications: 'Medications',
    emergencyContacts: 'Emergency Contacts',
    addEmergencyContact: 'Add Emergency Contact',
    personalInfo: 'Personal Information'
  },

  // Notifications section
  notifications: {
    tabs: {
      incoming: 'Incoming',
      reminders: 'Reminders',
      tasks: 'Tasks'
    },
    markAllAsRead: 'Mark All as Read',
    new: 'New',
    goToGame: 'Go to Game',
    noSearchResults: 'No search results found',
    noNotifications: 'No notifications yet',
    noReminders: 'No reminders yet',
    clearSearch: 'Clear Search',
    addReminder: 'Add Reminder',
    loading: 'Loading notifications...',
    reminder: {
      new: 'New Reminder',
      title: 'Title',
      titlePlaceholder: 'Reminder title',
      description: 'Description',
      descriptionPlaceholder: 'Description (optional)',
      date: 'Date',
      time: 'Time',
      category: 'Category',
      priority: 'Priority',
      repeat: 'Repeat',
      noRepeat: 'No repeat'
    },
    categories: {
      personal: 'Personal',
      work: 'Work',
      health: 'Health',
      finance: 'Finance',
      entertainment: 'Entertainment'
    },
    priorities: {
      low: 'Low',
      medium: 'Medium',
      high: 'High'
    },
    repeat: {
      daily: '🔄 Daily',
      weekly: '🔄 Weekly',
      monthly: '🔄 Monthly',
      yearly: '🔄 Yearly'
    },
    filters: {
      all: 'All',
      connections: 'Connections',
      messages: 'Messages',
      verifications: 'Verifications',
      security: 'Security',
      games: 'Games',
      system: 'System',
      active: 'Active',
      completed: 'Completed',
      highPriority: 'High Priority',
      mediumPriority: 'Medium Priority',
      lowPriority: 'Low Priority'
    },
    time: {
      justNow: 'Just now',
      minutesAgo: '{minutes} minutes ago',
      hoursAgo: '{hours} hours ago',
      daysAgo: '{days} days ago',
      today: 'Today {time}',
      tomorrow: 'Tomorrow {time}',
      futureDate: '{date} {time}',
      fullDate: '{day}/{month}/{year}'
    },
    errors: {
      loadFailed: 'Error loading notification data:'
    },
    demo: {
      connectionRequest: 'Sent a connection request',
      idappsVerification: 'IDapps Verification',
      qrVerified: 'Your QR code has been successfully verified',
      securityCenter: 'Security Center',
      newLogin: 'New login to your account from a new device',
      system: 'System',
      appUpdated: 'IDapps application updated, discover new features',
      sentMessage: 'Sent you a message: "Hello, how are you?"',
      gameHub: 'IDapps GameHub',
      newGame: 'New game: Crypto Adventure is live! Tournament with 50 IDCOIN reward has started.',
      connectionAccepted: 'Accepted your connection request',
      reminders: {
        weeklyMeeting: 'Weekly Meeting',
        teamMeeting: 'Team meeting - via Zoom',
        doctorAppointment: 'Doctor Appointment',
        dentalCheckup: 'Dental checkup - Ankara Dental Hospital',
        bills: 'Bills',
        utilityBills: 'Electricity and water bill payment',
        birthday: 'Birthday',
        mothersBirthday: 'Mother\'s birthday - Get a gift',
        gameTournament: 'GameHub Tournament',
        puzzleTournament: 'IDapps Blockchain Puzzle Tournament'
      }
    },
    tasks: {
      dailyTasks: 'Daily Tasks',
      wakeUp: 'Wake up at 7 AM',
      checkEmails: 'Check emails',
      reviewProject: 'Review project plan',
      completeGameTasks: 'Complete daily tasks in IDapps GameHub',
      workSchedule: 'Work Schedule',
      projectDevelopment: 'Project development',
      meetings: 'Meetings',
      codeReviews: 'Code reviews',
      gameTournament: 'GameHub Tournament',
      upcomingEvents: 'Upcoming Events',
      blockchainMeetup: 'Blockchain Meetup',
      web3Conference: 'Web3 Conference',
      onlineEvent: 'Online Event',
      idappsGameTournament: 'IDapps Game Tournament',
      metaverseEvent: 'Metaverse Event',
      goals: 'Goals',
      dailyStepGoal: 'Daily step goal',
      waterIntake: 'Water intake (ml)',
      workTime: 'Work time',
      idcoinGoal: 'IDCOIN Goal',
      hours: 'hours'
    }
  },

  // Blockchain and wallet connections
  blockchain: {
    connectWallet: 'Connect Wallet',
    walletConnected: 'Wallet Connected',
    walletAddress: 'Wallet Address',
    arweaveSync: 'Arweave Synchronization',
    backupToArweave: 'Backup to Arweave',
    permanentStorage: 'Permanent Storage',
    walletTitle: 'Blockchain Wallet',
    walletDescription: 'You can connect a blockchain wallet to secure your decentralized identity.',
    wallet: 'Wallet'
  },

  // Screen titles
  screens: {
    dashboardTitle: 'Home',
    qrScannerTitle: 'QR Code Scanner',
    gameHubTitle: 'Game Hub',
    messagingTitle: 'Messaging',
    notificationsTitle: 'Notifications',
    forgotPasswordTitle: 'Forgot Password',
    chatsTab: 'Chats',
    groupsTab: 'Groups'
  },

  // Messaging screen
  messaging: {
    searchChat: 'Search chat...',
    noSearchResults: 'No search results found',
    noResultsFor: 'No results for "{searchQuery}"',
    noGroupChats: 'You haven\'t created any group chats yet.',
    createNewGroup: 'Create New Group',
    encryptionDescription: 'All your messages in IDapps are end-to-end encrypted and can only be read by you and the person you\'re messaging with.',
    selfDestructMessages: 'Self-Destructing Messages',
    selfDestructDescription: 'Send self-destructing messages for your private conversations. They are automatically deleted after being read.',
    activateFeature: 'Activate Feature',
    aiAssistantPrivacy: 'AI Assistant Privacy',
    aiPrivacyDescription: 'Your messaging assistant runs completely on your device and your data is never sent to any server.',
    aiAssistantActive: 'AI Assistant Active',
    aiResponseSuggestions: 'AI Response Suggestions',
    writeMessage: 'Write a message...',
    online: 'Online',
    lastSeen: 'Last seen: Today 15:42',
    greeting: 'Hi, how are you?',
    imGood: 'I\'m good, you?',
    imGoodToo: 'I\'m good too, thanks.',
    sentToYou: 'I sent it to you.',
    canYouSendFiles: 'Can you send the files?',
    agreed: 'Agreed.',
    projectCostQuestion: 'Hello, can we talk about the cost of the project?',
    whatDoYouThink: 'Sure, what do you think?',
    priceProposal: 'I think 5000₺ is a reasonable price.',
    seeYou: 'Alright, see you.',
    meetingInvite: 'Tomorrow\'s meeting is at 15:00, can you attend?',
    callMeWhenFree: 'Could you call me when you\'re free?',
    aiSuggestion1: 'Yes, I\'ve completed it. I\'ll send it shortly.',
    aiSuggestion2: 'I\'m working on it, it will be ready tomorrow.',
    aiSuggestion3: 'I haven\'t finished it yet, I\'ll complete it this week.',
    aiSuggestion4: 'Sure, I\'ll call you in 10 minutes.',
    aiSuggestion5: 'I\'m in a meeting right now, can I call you in 1 hour?',
    aiSuggestion6: 'I\'m not available today, is it okay if I call you tomorrow?'
  },

  // Translations for GameHub section
  gameHub: {
    loadingGames: 'Loading games...',
    play: 'Play',
    join: 'Join',
    progress: 'Progress',
    level: 'Level {completed}/{total}',
    rewards: 'Rewards',
    points: 'points',
    totalPoints: 'Total Points',
    xpProgress: 'XP: {current}/{next}',
    featuredGame: 'Featured Game',
    categoriesTitle: 'Categories',
    allGames: 'All Games',
    seeAll: 'See All',
    dailyLogin: 'Daily Login',
    dayStreak: '{days} Day Streak',
    dailyLoginInfo: 'You can earn IDCOIN by logging in every day. Bonus reward for logging in 7 days in a row!',
    completed: '{completed}/{total} Completed',
    playerCount: '{count} Players',
    coinReward: '{amount} IDCOIN Reward',
    participants: 'Participants: {count}',
    startingGame: 'Starting {title} game...',

    errors: {
      loadFailed: 'Error loading GameHub data:'
    },

    tabs: {
      games: 'Games',
      daily: 'Daily',
      leaderboard: 'Leaderboard',
      rewards: 'Rewards'
    },

    categories: {
      quiz: 'Quiz',
      puzzle: 'Puzzle',
      collection: 'Collection',
      simulation: 'Simulation',
      strategy: 'Strategy'
    },

    games: {
      cryptoQuest: {
        title: 'Crypto Adventure',
        description: 'Progress through the crypto world, test your knowledge and earn IDCOIN!'
      },
      blockchainPuzzle: {
        title: 'Blockchain Puzzle',
        description: 'Solve puzzles about blockchain, complete smart contracts!'
      },
      nftCollector: {
        title: 'NFT Collector',
        description: 'Collect rare NFTs, complete your collection and win rewards!'
      },
      defiTrader: {
        title: 'DeFi Trader',
        description: 'Make DeFi transactions with virtual funds, develop the best strategy!'
      },
      daoManager: {
        title: 'DAO Manager',
        description: 'Manage your DAO, grow the community, make the right decisions!'
      }
    },

    dailyTasks: {
      title: 'Daily Tasks',
      completeAll: 'Complete all tasks, get a bonus!',
      completeAllBonus: 'When you complete all daily tasks, you\'ll get an extra {coins} IDCOIN bonus reward.',
      completeCryptoQuestLevels: 'Complete 3 levels in Crypto Adventure',
      solveBlockchainPuzzle: 'Solve a puzzle in Blockchain Puzzle',
      joinCryptoQuestTournament: 'Join the Crypto Adventure tournament',
      collectDailyIdcoin: 'Collect your daily IDCOIN reward',
      inviteFriend: 'Invite a friend to GameHub'
    },

    tournament: {
      active: 'Active Tournament',
      activeTournaments: 'Active Tournaments',
      ended: 'Ended',
      daysHoursLeft: '{days} days {hours} hours left',
      hoursMinutesLeft: '{hours} hours {minutes} minutes left',
      short: 'Tournament',
      joinAndWin: 'Join the tournament and get a chance to win {coins} IDCOIN!',
      gameTournament: '{game} Tournament'
    },

    leaderboard: {
      yourRank: 'Your Rank',
      rankPosition: 'Rank {rank}',
      topPercentage: 'You\'re in the top {percent}%!',
      weekly: 'Weekly',
      allTime: 'All Time',
      seeFullList: 'See Full List',
      gameLeaderboards: 'Game Leaderboards',
      you: 'You'
    },

    rewards: {
      balance: 'Balance',
      transfer: 'Transfer',
      transactions: 'Transactions',
      shop: 'Rewards Shop',
      cost: '{amount} IDCOIN',
      daily: 'Daily',
      completion: 'Completion',
      tournament: 'Tournament',

      items: {
        premium: {
          title: '1 Month Premium',
          description: 'Ad-free experience and extra features'
        },
        nft: {
          title: 'NFT Item',
          description: 'Special avatars and digital items'
        },
        xpBoost: {
          title: 'XP Boost',
          description: '2x experience points for 3 days'
        },
        cryptoGift: {
          title: 'Crypto Gift',
          description: 'Real cryptocurrency gift voucher'
        }
      }
    },

    achievements: {
      title: 'Achievements',
      completed: 'Completed',
      seeAll: 'See All Achievements',

      firstGame: 'First Game',
      firstGameDesc: 'Play your first game',
      firstTournament: 'First Tournament',
      firstTournamentDesc: 'Join a tournament',
      gameMaster: 'Game Master',
      gameMasterDesc: 'Play 5 different games',
      collector: 'Collector',
      collectorDesc: 'Earn 10 achievement badges'
    }
  },

  // Custom translations for WelcomeScreen
  welcomeScreen: {
    switchToLightMode: 'Switch to light mode',
    switchToDarkMode: 'Switch to dark mode',
    secureAuthentication: 'Secure authentication',
    quickLoginTip: 'Access your account', // Added
    createAccountTip: 'Join the decentralized identity revolution' // Added
  },

  // IdentityFeed translations
  identityCircle: {
    feed: {
      // Create options
      mediaShare: 'Share Media',
      mediaDescription: 'Photos, videos and moments',
      thoughtShare: 'Share Thought',
      thoughtDescription: 'Ideas and thoughts',
      voiceNote: 'Voice Note',
      voiceDescription: 'Voice messages and stories',
      statsShare: 'Share Statistics',
      statsDescription: 'Achievements and metrics',
      digitalVault: 'Digital Vault',
      vaultDescription: 'Private and encrypted storage',

      // Privacy options
      privacyTitle: 'Privacy Settings',
      privacyDescription: 'Who can see your content?',
      circle: 'Circle',
      circleDescription: 'Visible to all circle members',
      closeCircle: 'Close Circle',
      closeDescription: 'Only close friends can see',
      selectedPeople: 'Selected People',
      selectedDescription: 'People you choose can see',
      vaultPrivacy: 'Digital Vault',
      vaultPrivacyDescription: 'Only you, encrypted',

      // Post actions
      like: 'Like',
      comment: 'Comment',
      share: 'Share',
      bookmark: 'Bookmark',
      commentPlaceholder: 'Add a comment...',
      you: 'You',

      // Create modal
      createTitle: 'Create Content',
      createDescription: 'Share your thoughts with the world',
      createPlaceholder: 'What\'s on your mind?',

      // Profile modal
      level: 'Level',
      xpProgress: 'XP Progress',
      compatibility: 'compatibility',
      message: 'Message',
      profile: 'Profile',
      mute: 'Mute',
      recentActivity: 'Recent Activity',
      mutualConnections: 'Mutual Connections',
      more: 'more',
      swipeToClose: 'Swipe down to close',

      // General
      close: 'Close',
      cancel: 'Cancel',
      confirm: 'Confirm',
      continue: 'Continue',
      ago: 'ago',
      justNow: 'just now',
      voiceNoteLabel: 'Voice Note',
      lastSeen: 'Last seen unknown',

      // Share modal
      sharePostTitle: 'Share Post',
      sharePostDescription: 'Share this amazing content with your circle',

      // Statistics sharing
      shareStatsReady: 'statistics selected, ready to share!',
      shareStatsButton: 'Share Statistics',
      selectStatsToShare: 'Select statistics you want to share',
      sharing: 'Sharing...',
      sharingProgress: 'Sharing my progress report! 📈',
      statisticsReport: 'Statistics Report',

      // Statistics categories
      generalLevelXP: 'General Level and XP',
      basicStatistics: 'Basic Statistics',

      // Metric names
      generalLevel: 'General Level',
      generalXP: 'General XP',
      averageSleep: 'Average Sleep',
      dailySteps: 'Daily Steps',
      focusTime: 'Focus Time',
      completedTasks: 'Completed Tasks',
      activeDays: 'Active Days',
      totalTasks: 'Total Tasks',
      earnedBadges: 'Earned Badges',
      currentStreak: 'Current Streak',
      completedGoals: 'Completed Goals',

      // Units
      hours: 'hours',
      minutes: 'min',
      days: 'days',
      badges: 'badges',
      goals: 'goals',

      // Achievement titles
      morningRunAchievement: 'Morning Run Achievement',
      achievements: 'Achievements',

      // Additional categories
      qrStatistics: 'QR Statistics',
      scansPerformed: 'Scans Performed',
      successfulConnections: 'Successful Connections',
      qrCodesGenerated: 'QR Codes Generated',
      totalQR: 'Total QR',
      qrViews: 'QR Views',
      health: 'Health',
      productivity: 'Productivity',

      // Time-based metrics
      weeks: 'weeks',
      months: 'months',
      scans: 'scans',
      connections: 'connections',
      codes: 'codes',
      qrStatistics: 'QR Statistics',
      totalQR: 'Total QR',
      qrViews: 'QR Views',
      health: 'Health'
    }
  }
};